/**
 * Seeded pseudo-random number generator.
 *
 * Non-negotiable #7: *all* randomness in OpenSkies goes through a seeded RNG so that a
 * session can be replayed, lock-stepped across a network, or reproduced from a bug report.
 * `Math.random()` is banned outside of tooling.
 *
 * Algorithm: sfc32 (Chris Doty-Humphrey's Small Fast Counting generator, 32-bit variant).
 * ~2^128 period, passes PractRand, four uint32 words of state → trivially serialisable.
 * Seeded through splitmix32 so that even a weak seed (0, 1, "a") produces good state.
 */

/** Serialisable RNG state — plain numbers so it survives `structuredClone`/JSON. */
export interface RngState {
  a: number;
  b: number;
  c: number;
  d: number;
  /** Cached second normal deviate from the last `gaussian()` call, or null. */
  gaussianSpare: number | null;
}

/** splitmix32 — used to expand a single seed word into generator state. */
function splitmix32(seed: number): () => number {
  let x = seed >>> 0;
  return () => {
    x = (x + 0x9e3779b9) >>> 0;
    let z = x;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
}

/** FNV-1a — turns a string seed into a uint32. */
export function hashStringToUint32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export class Rng {
  private a = 0;
  private b = 0;
  private c = 0;
  private d = 0;
  /**
   * Cached second deviate from the last `gaussian()` call, kept as two fields rather than a
   * `number | null`. A field that holds both a double and `null` has Tagged representation in
   * V8, so every store boxes the double into a fresh HeapNumber — 8 B of garbage per call,
   * which the allocation guard in alloc.test.ts catches. Splitting the flag out keeps the
   * numeric field in Double representation, where stores mutate in place and allocate nothing.
   */
  private gaussianSpare = 0;
  private hasGaussianSpare = false;

  constructor(seed: number | string = 0x5ee4) {
    this.reseed(seed);
  }

  /** Reset the generator to a fresh sequence derived from `seed`. */
  reseed(seed: number | string): void {
    const s = typeof seed === 'string' ? hashStringToUint32(seed) : seed >>> 0;
    const next = splitmix32(s);
    this.a = next();
    this.b = next();
    this.c = next();
    this.d = next();
    this.hasGaussianSpare = false;
    // Discard the first few outputs so low-entropy seeds do not correlate.
    for (let i = 0; i < 12; i++) this.nextUint32();
  }

  /** Raw generator step. Returns a uint32 in [0, 2^32). Allocation-free. */
  nextUint32(): number {
    const t = (this.a + this.b + this.d) >>> 0;
    this.d = (this.d + 1) >>> 0;
    this.a = (this.b ^ (this.b >>> 9)) >>> 0;
    this.b = (this.c + (this.c << 3)) >>> 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) >>> 0;
    return t;
  }

  /** Uniform float in [0, 1). */
  nextFloat(): number {
    return this.nextUint32() / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.nextFloat();
  }

  /** Uniform integer in [min, max] (both inclusive). */
  int(min: number, max: number): number {
    return min + Math.floor(this.nextFloat() * (max - min + 1));
  }

  /** True with probability `p` (default 0.5). */
  bool(p = 0.5): boolean {
    return this.nextFloat() < p;
  }

  /** Uniform float in [-1, 1). */
  signed(): number {
    return this.nextFloat() * 2 - 1;
  }

  /**
   * Normal deviate via the Marsaglia polar method.
   * The unused second deviate is cached (and serialised) so sequences replay exactly.
   */
  gaussian(mean = 0, stdDev = 1): number {
    if (this.hasGaussianSpare) {
      this.hasGaussianSpare = false;
      return mean + stdDev * this.gaussianSpare;
    }
    let u: number;
    let v: number;
    let s: number;
    do {
      u = this.signed();
      v = this.signed();
      s = u * u + v * v;
    } while (s >= 1 || s === 0);
    const scale = Math.sqrt((-2 * Math.log(s)) / s);
    this.gaussianSpare = v * scale;
    this.hasGaussianSpare = true;
    return mean + stdDev * u * scale;
  }

  /** Pick a uniformly random element. Throws on an empty array. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)];
  }

  /** In-place Fisher–Yates shuffle. Allocation-free. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }
    return items;
  }

  /** Snapshot the generator so a session can be resumed or replayed. Allocates — not hot-path. */
  getState(): RngState {
    return {
      a: this.a,
      b: this.b,
      c: this.c,
      d: this.d,
      gaussianSpare: this.hasGaussianSpare ? this.gaussianSpare : null,
    };
  }

  /** Restore a snapshot taken by {@link getState}. */
  setState(state: RngState): void {
    this.a = state.a >>> 0;
    this.b = state.b >>> 0;
    this.c = state.c >>> 0;
    this.d = state.d >>> 0;
    this.hasGaussianSpare = state.gaussianSpare !== null;
    this.gaussianSpare = state.gaussianSpare ?? 0;
  }

  /** A child generator deterministically derived from this one (per-subsystem streams). */
  fork(): Rng {
    return new Rng(this.nextUint32());
  }
}

/**
 * The one RNG the simulation shares. Reseed it at session start (from a lobby seed, a
 * replay header, or the clock) and every subsystem replays identically.
 */
export const rng = new Rng(0x5ee4);
